/**
 * KycSettings — admin toggles which KYC checks are mandatory before
 * an onboarding invite can be marked "completed".
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Settings, Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { getKycSettings, updateKycSettings } from '../api';

const KYC_TYPES = [
  { value: 'pan', label: 'PAN Verification' },
  { value: 'bank', label: 'Bank Account (Penny-drop)' },
  { value: 'ifsc', label: 'IFSC Lookup' },
  { value: 'mobile_otp', label: 'Mobile OTP (MSG91)' },
  { value: 'email_otp', label: 'Email OTP' },
  { value: 'aadhaar_otp', label: 'Aadhaar OTP eKYC' },
  { value: 'ocr_aadhaar', label: 'Aadhaar OCR' },
  { value: 'ocr_pan', label: 'PAN OCR' },
  { value: 'ocr_passport', label: 'Passport OCR' },
  { value: 'ocr_driving_license', label: 'Driving License OCR' },
  { value: 'ocr_educational_certificate', label: 'Education Certificate OCR' },
];

export default function KycSettings() {
  const [required, setRequired] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ updated_at: null, updated_by_name: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getKycSettings();
        if (!cancelled) {
          setRequired(data.required_for_onboarding || []);
          setMeta({ updated_at: data.updated_at, updated_by_name: data.updated_by_name });
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Failed to load KYC settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = (value) => {
    setRequired((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await updateKycSettings({ required_for_onboarding: required });
      setMeta({ updated_at: data.updated_at, updated_by_name: data.updated_by_name });
      toast.success('Onboarding KYC requirements updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="kyc-settings-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Settings size={16} className="text-slate-500" />
          Onboarding KYC Requirements
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Select which checks must pass before a new hire&apos;s onboarding portal can mark itself
          &quot;completed&quot;. Leave all unchecked to disable KYC gating entirely.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="kyc-settings-checkboxes">
              {KYC_TYPES.map((t) => (
                <label
                  key={t.value}
                  className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 cursor-pointer hover:bg-slate-50"
                  data-testid={`kyc-settings-toggle-${t.value}`}
                >
                  <Checkbox
                    checked={required.includes(t.value)}
                    onCheckedChange={() => toggle(t.value)}
                    data-testid={`kyc-settings-checkbox-${t.value}`}
                  />
                  <Label className="text-sm cursor-pointer">{t.label}</Label>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3 pt-2 border-t">
              <div className="text-xs text-slate-500">
                {meta.updated_at
                  ? `Last updated: ${new Date(meta.updated_at).toLocaleString()} · ${meta.updated_by_name}`
                  : 'Not configured yet'}
              </div>
              <Button onClick={handleSave} disabled={saving} data-testid="kyc-settings-save-btn">
                {saving
                  ? <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</>
                  : <><Save size={14} className="mr-2" /> Save Requirements</>}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
